/**
 * CompanySection - 회사 전환 및 추가
 * Settings 페이지에서 소속 회사 관리 기능 제공
 * - 소속 회사 목록 표시
 * - 회사 전환
 * - 다른 회사 추가 (검색 및 가입 신청)
 */
import { useState, useCallback } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { useAuth } from "../../hooks/useAuth";
import {
  MY_COMPANIES,
  SEARCH_COMPANIES,
  SET_CURRENT_COMPANY,
  REQUEST_JOIN_COMPANY,
  type MyCompaniesData,
  type SetCurrentCompanyData,
  type SearchCompaniesData,
  type MyCompany,
  type CompanySearchResult,
} from "../../lib/graphql/user";
import { setAuthToken, clearAllCaches } from "../../lib/apollo";

type ViewMode = "list" | "search";

export function CompanySection() {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const [requestingTo, setRequestingTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 내 회사 목록 조회
  const {
    data: companiesData,
    loading: companiesLoading,
    refetch,
  } = useQuery<MyCompaniesData>(MY_COMPANIES, {
    fetchPolicy: "cache-and-network",
  });

  // 회사 검색
  const [searchCompanies, { data: searchData, loading: searching }] =
    useLazyQuery<SearchCompaniesData>(SEARCH_COMPANIES, {
      fetchPolicy: "network-only",
    });

  // 회사 전환
  // Issue #466: 토큰 저장 + 캐시 정리 후 새로고침
  const [setCurrentCompany] = useMutation<SetCurrentCompanyData>(SET_CURRENT_COMPANY, {
    onCompleted: async (data) => {
      const { token } = data.setCurrentCompany;

      // 1. 새 JWT 토큰 저장 (Capacitor Preferences + 메모리 캐시)
      if (!token) {
        setError("토큰이 반환되지 않았습니다. 다시 시도해주세요.");
        setSwitchingTo(null);
        return;
      }
      setAuthToken(token);

      // 2. Apollo 캐시 + IndexedDB 정리 (이전 회사 데이터 제거)
      await clearAllCaches();

      // 3. 전체 앱 새로고침 (새 토큰으로 재초기화)
      window.location.reload();
    },
    onError: (err) => {
      setError(err.message || "회사 전환에 실패했습니다.");
      setSwitchingTo(null);
    },
  });

  // 가입 신청
  const [requestJoinCompany] = useMutation(REQUEST_JOIN_COMPANY, {
    onCompleted: () => {
      setSuccess("가입 신청이 완료되었습니다. 관리자 승인을 기다려주세요.");
      setRequestingTo(null);
      refetch();
      // 검색 결과 갱신
      if (searchKeyword) {
        handleSearch();
      }
      // 3초 후 성공 메시지 숨김
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err) => {
      setError(err.message || "가입 신청에 실패했습니다.");
      setRequestingTo(null);
    },
  });

  const myCompanies = companiesData?.myCompanies ?? [];
  const searchResults = searchData?.searchCompanies ?? [];
  const currentCompanyId = user?.companyId;

  // 회사 전환 핸들러
  const handleSwitchCompany = async (companyId: string) => {
    if (companyId === currentCompanyId || switchingTo) return;

    setSwitchingTo(companyId);
    setError(null);
    await setCurrentCompany({
      variables: { companyId },
    });
  };

  // 검색 핸들러
  const handleSearch = useCallback(() => {
    const keyword = searchKeyword.trim();
    if (!keyword) return;

    setError(null);
    setHasSearched(true);
    searchCompanies({
      variables: {
        input: { keyword, limit: 10 },
      },
    });
  }, [searchKeyword, searchCompanies]);

  // Enter 키 검색
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 가입 신청 핸들러
  const handleRequestJoin = async (company: CompanySearchResult) => {
    if (!company.canRequest || requestingTo) return;

    setRequestingTo(company._id);
    setError(null);
    await requestJoinCompany({
      variables: {
        input: { companyId: company._id },
      },
    });
  };

  // 검색 모드 전환
  const handleOpenSearch = () => {
    setViewMode("search");
    setSearchKeyword("");
    setHasSearched(false);
    setError(null);
    setSuccess(null);
  };

  const handleCloseSearch = () => {
    setViewMode("list");
    setSearchKeyword("");
    setHasSearched(false);
    setError(null);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* 섹션 헤더 */}
      <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">소속 회사</h2>
          {viewMode === "list" && (
            <p className="mt-1 text-xs text-gray-500">회사를 탭하여 전환할 수 있습니다</p>
          )}
        </div>
        {viewMode === "search" && (
          <button
            type="button"
            onClick={handleCloseSearch}
            className="text-sm text-gray-500 hover:text-gray-700 min-h-[44px] px-2"
          >
            취소
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* 에러 메시지 */}
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* 성공 메시지 */}
        {success && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-3">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        {/* 목록 뷰 */}
        {viewMode === "list" && (
          <>
            {/* 로딩 상태 */}
            {companiesLoading && myCompanies.length === 0 ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse rounded-lg border border-gray-200 p-4">
                    <div className="h-4 w-32 rounded bg-gray-200" />
                    <div className="mt-2 h-3 w-20 rounded bg-gray-200" />
                  </div>
                ))}
              </div>
            ) : myCompanies.length === 0 ? (
              <div className="text-center py-6">
                <BuildingIcon className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">소속된 회사가 없습니다.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {myCompanies.map((company) => (
                  <CompanyItem
                    key={company._id}
                    company={company}
                    isCurrent={company.companyId === currentCompanyId}
                    isSwitching={switchingTo === company.companyId}
                    onSwitch={() => handleSwitchCompany(company.companyId)}
                  />
                ))}
              </div>
            )}

            {/* 다른 회사 추가 버튼 */}
            <button
              type="button"
              onClick={handleOpenSearch}
              className="w-full min-h-[44px] px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 active:bg-blue-100 flex items-center justify-center gap-2"
            >
              <PlusIcon className="w-4 h-4" />
              다른 회사 추가
            </button>
          </>
        )}

        {/* 검색 뷰 */}
        {viewMode === "search" && (
          <>
            {/* 검색 입력 */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="사업자번호 또는 회사명"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full min-h-[44px] pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
              <button
                type="button"
                onClick={handleSearch}
                disabled={searching || !searchKeyword.trim()}
                className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {searching ? "..." : "검색"}
              </button>
            </div>

            {/* 검색 결과 */}
            {searching ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                <p className="mt-2 text-sm text-gray-500">검색 중...</p>
              </div>
            ) : hasSearched && searchResults.length === 0 ? (
              <div className="text-center py-6">
                <BuildingIcon className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">검색 결과가 없습니다.</p>
                <p className="text-xs text-gray-400">다른 검색어로 시도해 보세요.</p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-2">
                {searchResults.map((company) => (
                  <SearchResultItem
                    key={company._id}
                    company={company}
                    isRequesting={requestingTo === company._id}
                    onRequest={() => handleRequestJoin(company)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <SearchIcon className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">회사를 검색해 주세요</p>
                <p className="text-xs text-gray-400">
                  사업자번호 또는 회사명으로 검색할 수 있습니다.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// 회사 목록 아이템
interface CompanyItemProps {
  company: MyCompany;
  isCurrent: boolean;
  isSwitching: boolean;
  onSwitch: () => void;
}

function CompanyItem({ company, isCurrent, isSwitching, onSwitch }: CompanyItemProps) {
  const isPending = company.status === "pending";

  return (
    <button
      type="button"
      onClick={onSwitch}
      disabled={isCurrent || isPending || isSwitching}
      className={`w-full min-h-[56px] px-4 py-3 rounded-lg border text-left transition-colors ${
        isCurrent
          ? "border-blue-500 bg-blue-50"
          : isPending
            ? "border-gray-200 bg-gray-50 cursor-not-allowed"
            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50 active:bg-gray-100"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BuildingIcon className={`w-5 h-5 ${isCurrent ? "text-blue-600" : "text-gray-400"}`} />
          <div>
            <p className={`text-sm font-medium ${isCurrent ? "text-blue-900" : "text-gray-900"}`}>
              {company.companyName}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              {company.role === "manager" && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                  관리자
                </span>
              )}
              {isPending && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">
                  승인 대기중
                </span>
              )}
            </div>
          </div>
        </div>
        {isSwitching ? (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
        ) : isCurrent ? (
          <CheckIcon className="w-5 h-5 text-blue-600" />
        ) : null}
      </div>
    </button>
  );
}

// 검색 결과 아이템
interface SearchResultItemProps {
  company: CompanySearchResult;
  isRequesting: boolean;
  onRequest: () => void;
}

function SearchResultItem({ company, isRequesting, onRequest }: SearchResultItemProps) {
  return (
    <div
      className={`rounded-lg border p-4 ${
        company.canRequest ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50 opacity-75"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{company.name}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            {company.businessNumber && (
              <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                {company.businessNumber}
              </span>
            )}
            {company.ceoName && <span>대표: {company.ceoName}</span>}
            <span className="flex items-center gap-1">
              <UserIcon className="w-3 h-3" />
              {company.memberCount}명
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onRequest}
          disabled={!company.canRequest || isRequesting}
          className={`min-h-[36px] px-3 py-1.5 text-xs font-medium rounded-lg flex-shrink-0 ${
            company.canRequest
              ? "text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
              : "text-gray-400 bg-gray-100 cursor-not-allowed"
          }`}
        >
          {isRequesting ? (
            <span className="flex items-center gap-1">
              <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
              신청 중
            </span>
          ) : company.canRequest ? (
            "가입 신청"
          ) : (
            "신청 불가"
          )}
        </button>
      </div>
    </div>
  );
}

// 아이콘 컴포넌트 (lucide-react 대신 인라인 SVG로 의존성 최소화)
function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

export default CompanySection;
